import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import * as Analytics from 'analytics/Analytics';
import {
  ANALYTICS_MODULE,
  AnalyticsMetadataForContext,
  COMMON_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import {
  canProceedWithSamples,
  checkForSampleStatusWarnings,
  SampleStatusWarning,
} from 'inventory/sample-status/SampleStatusWarning';
import {SampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import InlineBanner, {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import Modal from 'ui-library/modals/Modal';
import {InputChangeEvent} from 'util/Types';

export interface BaseBulkEditSamplesModalProps {
  modalContent: JSX.Element;
  modalTitle: string;
  onRequestClose: () => void;
  onRequestSubmit: (selectedIds: Array<string>) => void;
  analyticsMetadata?: AnalyticsMetadataForContext;
  itemIds: Array<string>;
  /** If provided, these params will be used to check for status warnings. */
  sampleStatusParams?: {
    trackingParams: SampleStatusWarningTrackingParams;
    /** Setting to true renders the status table to allow de-selecting items, even if status warnings would
     * not otherwise apply. */
    shouldAlwaysShowSampleStatusTable?: boolean;
  };
  /** Any additional checks for disabling submit. By default, we use the can proceed with samples status checks. */
  isSubmitDisabled?: () => boolean;
  dataTestAttrs?: DataTestAttrs;
  clearFieldCheckboxProps?: {
    shouldShowClearCheckbox: () => boolean;
    shouldClear: boolean;
    onChangeShouldClear: (clearField: boolean) => void;
  };
  infoNoticeProps?: Omit<InlineBannerProps<InlineBannerType.INFO>, 'bannerType'>;
  scrollable?: boolean;
  expandHeight?: boolean;
}

/**
 * Base bulk editor modal that handles checking for status warnings & rendering actual content correspondingly.
 *
 * Checks for status warnings & blocks until overridden/items deselected when `sampleStatusParams`
 * is provided. Otherwise, just renders the corresponding content.
 *
 * Handles tracking open/close/submit modal analytics.
 */
export default function BaseBulkEditSamplesModal(props: BaseBulkEditSamplesModalProps): JSX.Element {
  const [canProceedOnSampleStatus, setCanProceedOnSampleStatus] = useState(false);
  const [selectedSampleIds, setSelectedSampleIds] = useState<Array<string>>([]);

  const [shouldShowStatusWarnings, setShouldShowStatusWarnings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [samples, setSamples] = useState<Array<SampleStatusItem> | null>(null);
  const sampleIds = props.sampleStatusParams?.trackingParams?.sampleIds ?? null;

  useOnMount(() => {
    async function checkForWarnings(): Promise<void> {
      if (sampleIds != null) {
        const {shouldShowWarning, samples} = await checkForSampleStatusWarnings({
          sampleIds,
          shouldAlwaysShowWarning: props.sampleStatusParams?.shouldAlwaysShowSampleStatusTable,
        });
        setShouldShowStatusWarnings(shouldShowWarning);
        setSamples(samples ?? null);
        setSelectedSampleIds(sampleIds);
        setLoading(false);
      } else {
        setLoading(false);
      }
    }
    checkForWarnings();

    Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.SAMPLES, {
      ...props.analyticsMetadata,
      itemIds: props.itemIds,
      numItemsSelected: props.itemIds.length,
    });
  });

  const possibleWarning =
    shouldShowStatusWarnings && sampleIds != null && samples != null ? (
      <SampleStatusWarning
        sampleIds={sampleIds}
        selectedSampleIds={selectedSampleIds}
        setSelectedSampleIds={setSelectedSampleIds}
        shouldProceed={canProceedOnSampleStatus}
        setShouldProceed={setCanProceedOnSampleStatus}
        trackingParams={props.sampleStatusParams!.trackingParams}
        samples={samples}
      />
    ) : null;

  const canProceedWithPossibleSamples = (): boolean =>
    props.sampleStatusParams == null ||
    canProceedWithSamples(selectedSampleIds, shouldShowStatusWarnings, canProceedOnSampleStatus);

  const handleRequestSubmit = (): void => {
    const itemIds = sampleIds != null ? selectedSampleIds : props.itemIds;
    Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL, ANALYTICS_MODULE.SAMPLES, {
      ...props.analyticsMetadata,
      itemIds,
      numItemsSelected: itemIds.length,
    });
    props.onRequestSubmit(itemIds);
  };

  const handleRequestClose = (): void => {
    props.onRequestClose();
    Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, ANALYTICS_MODULE.SAMPLES, {
      ...props.analyticsMetadata,
      itemIds: props.itemIds,
      numItemsSelected: props.itemIds.length,
    });
  };

  return (
    <Modal
      title={props.modalTitle}
      onRequestClose={handleRequestClose}
      buttons={[
        {
          text: 'Update',
          submit: true,
          disabled: props.isSubmitDisabled?.() || !canProceedWithPossibleSamples(),
        },
      ]}
      onRequestSubmit={handleRequestSubmit}
      dataTestAttrs={props.dataTestAttrs}
      scrollable={props.scrollable}
      expandHeight={props.expandHeight}
    >
      {loading ? (
        <CenteredHorizontalThrobber />
      ) : (
        <>
          {possibleWarning}
          {canProceedWithPossibleSamples() && (
            <div className={shouldShowStatusWarnings ? css(styles.formPadding) : undefined}>
              {props.modalContent}
              {props.clearFieldCheckboxProps != null &&
                props.clearFieldCheckboxProps.shouldShowClearCheckbox() && (
                  <Row>
                    <Col md={6}>
                      <CheckboxInput
                        checked={props.clearFieldCheckboxProps.shouldClear}
                        id="clear-field-value"
                        label="Clear values"
                        onChange={(e: InputChangeEvent) => {
                          props.clearFieldCheckboxProps!.onChangeShouldClear(e.target.checked);
                        }}
                      />
                    </Col>
                  </Row>
                )}
              {props.infoNoticeProps != null && (
                <Row className={css(styles.formPadding)}>
                  <Col md={12}>
                    <InlineBanner bannerType={InlineBannerType.INFO} {...props.infoNoticeProps} />
                  </Col>
                </Row>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  currentValue: {paddingTop: '5px'},
  formPadding: {paddingTop: '15px'},
});
